import './index.css'

import * as React from 'react'
import { Link } from 'react-router-dom'

type OwnProps = {
  to?:
    | string
    | {
        pathname: string
        query?: Record<string, string>
        search?: string
      }
  children: React.ReactNode
  target?: '_blank' | '_self' | '_parent' | '_top'
  rel?: string
  onClick?: (...args: Array<any>) => any
  onAuxClick?: React.MouseEventHandler<HTMLAnchorElement>
  className?: string
}

/**
 * An inline link component that automatically handles both internal routing and external links.
 * Uses React Router's Link for internal navigation and regular anchor tags for external URLs.
 *
 * @param to - The destination URL or route object. External URLs (starting with 'http') use anchor tags
 * @param children - The content to display inside the link
 * @param target - The target attribute for the link (e.g., '_blank', '_self')
 * @param rel - The rel attribute for the link (e.g., 'noopener', 'noreferrer')
 * @param onClick - Optional click handler function
 * @param onAuxClick - Optional handler for non-primary clicks (e.g. a middle-click opening a new tab), which fire no click event
 * @param className - Optional CSS class name
 * @returns An anchor tag for external URLs or React Router Link for internal routes
 */
const InlineLink = ({
  to,
  children,
  onClick,
  ...rest
}: OwnProps): React.ReactNode | React.ReactElement<'a'> =>
  typeof to === 'string' && to.startsWith('http') ? (
    <a className="inline-link" href={to} onClick={onClick} {...rest}>
      {children}
    </a>
  ) : (
    <Link className="inline-link" to={to ?? '#'} onClick={onClick} {...rest}>
      {children}
    </Link>
  )

export default InlineLink
